import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom';
import { DragDropProvider } from '@dnd-kit/react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import { useBlocker } from 'react-router';

import presets from '@/components/Dialog/presets';
import { Z_INDEX } from '@/constants/z-index';
import { PageInformationContext } from '@/context/PageInformationContext';
import { RemoveWidgetProvider } from '@/context/RemoveWidgetContext';
import { useDeleteDashboard, useSaveDashboard } from '@/features/Dashboard/api/mutations';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import AppsOrder from '@/features/Dashboard/pages/Edit/components/AppsOrder';
import { EditBanner } from '@/features/Dashboard/pages/Edit/components/EditBanner';
import EditDashboardName from '@/features/Dashboard/pages/Edit/components/EditDashboardName';
import { TilesOrder } from '@/features/Dashboard/pages/Edit/components/TilesOrder';
import { buildPreviewConfig } from '@/features/Dashboard/pages/Edit/model/utils/mapping';
import { useMarketPreview } from '@/features/Markets/api';
import { useAppSelector } from '@/hooks/redux-hooks';
import { useDisclosure } from '@/hooks/useDisclosure';

import { DashboardLayout } from '../../components/DashboardLayout';
import { ConfirmationDialog, EditableTabLabel, EditActionButtons } from './components';
import { useDashboardEditActions } from './hooks/useDashboardEditActions';
import { useDashboardEditInit } from './hooks/useDashboardEditInit';
import {
  selectCurrentMarketTabApps,
  selectCurrentTabIndex,
  selectCurrentTiles,
  selectDraftTabNames,
  selectDraftTabs,
  selectHasChanges,
  selectHasTabNameErrors,
  selectInitialDashboardName
} from './model/dashboardEdit.selectors';

import type { DashboardTab, MarketApps } from '../../types';

export const EditDashboard = ({
  onClose,
  dashboardName: currentDashboardName,
  initialDashboardName: initialNameProp,
  dashboardConfig,
  activeTab = 0
}: {
  onClose: () => void;
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  dashboardStructure?: DashboardTab | null;
  activeTab?: number;
}) => {
  // Initialize draft from React Query cache
  const { isInitialized, isLoading: isInitLoading } = useDashboardEditInit({
    dashboardName: currentDashboardName,
    initialDashboardName: initialNameProp,
    dashboardConfig,
    activeTabIndex: activeTab
  });

  // Selectors
  const draftTabs = useAppSelector(selectDraftTabs);
  const draftTabNames = useAppSelector(selectDraftTabNames);
  const hasChanges = useAppSelector(selectHasChanges);
  const hasTabNameErrors = useAppSelector(selectHasTabNameErrors);
  const currentTabIndex = useAppSelector(selectCurrentTabIndex);
  const currentTiles = useAppSelector(selectCurrentTiles);
  const currentMarketTabApps = useAppSelector(selectCurrentMarketTabApps);

  const previewConfig = useMemo(
    () => buildPreviewConfig(currentTiles, currentMarketTabApps),
    [currentTiles, currentMarketTabApps]
  );
  const { data: previewData } = useMarketPreview(previewConfig);

  // Actions
  const { renameTab, addTab, deleteTab, reorderTabs, setCurrentTab, resetToEmpty } =
    useDashboardEditActions();

  // Save/Delete mutations
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const { mutate: saveMutate, isPending: isSaving, isSuccess: isSaveSuccess } = useSaveDashboard();
  const { mutate: deleteDashboard, isPending: isDeletingDashboard } =
    useDeleteDashboard(initialDashboardName);

  const { setMode } = useAppForm();

  useEffect(() => {
    setMode('edit');
  }, [setMode]);

  const tabHandleRefsMap = useRef(new Map<string, React.RefObject<HTMLDivElement | null>>());

  const getTabHandleRef = useCallback((tabId: string): React.RefObject<HTMLDivElement | null> => {
    let ref = tabHandleRefsMap.current.get(tabId);
    if (!ref) {
      ref = createRef<HTMLDivElement>();
      tabHandleRefsMap.current.set(tabId, ref);
    }
    return ref;
  }, []);

  const discardDialog = useDisclosure();
  const removeAllDialog = useDisclosure();
  const removeDashboardDialog = useDisclosure();
  const removeWidgetDialog = useDisclosure();
  const [removeWidgetCallback, setRemoveWidgetCallback] = useState<(() => void) | null>(null);
  const [hasDashboardNameErrors, setHasDashboardNameErrors] = useState(false);

  useEffect(() => {
    if (isSaveSuccess) {
      onClose();
    }
  }, [isSaveSuccess, onClose]);

  const handleRemoveAll = useCallback(() => {
    resetToEmpty();
    removeAllDialog.close();
  }, [resetToEmpty, removeAllDialog.close]);

  const handleRemoveDashboard = useCallback(() => {
    deleteDashboard();
  }, [deleteDashboard]);

  const openRemoveModal = useCallback(
    (onOk: () => void) => {
      setRemoveWidgetCallback(() => onOk);
      removeWidgetDialog.open();
    },
    [removeWidgetDialog.open]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const isSaveDisabled = isSaving || !hasChanges || hasDashboardNameErrors || hasTabNameErrors;

  const handleSave = useCallback(() => {
    saveMutate();
  }, [saveMutate]);

  const tabsConfig = useMemo(() => {
    return draftTabs.map(tab => {
      const handleRef = getTabHandleRef(tab.id);
      return {
        id: tab.id,
        name: tab.name,
        sortableHandleRef: handleRef,
        label: (
          <EditableTabLabel
            allowTabReorder
            canDelete={draftTabs.length > 1}
            dragHandleRef={handleRef}
            name={tab.name}
            tabId={tab.id}
            onDelete={deleteTab}
            onRename={renameTab}
          />
        )
      };
    });
  }, [draftTabs, deleteTab, renameTab, getTabHandleRef]);

  const handleAddTabDraft = useCallback(() => {
    const tabId = crypto.randomUUID();
    // The slice will automatically generate a unique name using generateUniqueTabName
    addTab(tabId, TAB_DEFAULT_BASE_NAME);
  }, [addTab]);

  const handleDraftTabChange = useCallback(
    (index: number) => {
      const tabId = draftTabs[index]?.id;
      if (tabId) {
        setCurrentTab(tabId);
      }
    },
    [draftTabs, setCurrentTab]
  );

  const handleTabDragEnd = useCallback(
    (event: any) => {
      if (event.canceled) return;

      const { source, target } = event.operation;
      if (source?.type !== 'dashboard-tab') return;

      let fromIndex: number;
      let toIndex: number;

      if (target?.type === 'dashboard-tab' && target.id !== source.id) {
        fromIndex = draftTabs.findIndex(t => t.id === source.id);
        toIndex = draftTabs.findIndex(t => t.id === target.id);
      } else if (typeof source.index === 'number' && typeof source.initialIndex === 'number') {
        fromIndex = source.initialIndex;
        toIndex = source.index;
      } else {
        return;
      }

      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        reorderTabs(fromIndex, toIndex);
      }
    },
    [draftTabs, reorderTabs]
  );

  const handleTabCreated = useCallback(
    (tabName: string, tabId: string) => {
      addTab(tabId, tabName);
    },
    [addTab]
  );

  const handleDiscardClick = useCallback(() => {
    if (hasChanges) {
      discardDialog.open();
    } else {
      handleClose();
    }
  }, [hasChanges, handleClose, discardDialog.open]);

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();

  const handleAddAppClick = useCallback(() => {
    setIsEditMode(true);
    setEditingTarget(null);
    setDrawerStep(DrawerStep.SelectApp);
  }, [setDrawerStep, setEditingTarget, setIsEditMode]);

  const removeWidgetValue = useMemo(() => {
    return {
      openRemoveModal
    };
  }, [openRemoveModal]);

  const blocker = useBlocker(() => {
    return !isDeletingDashboard && hasChanges && !isSaveSuccess && !isSaving;
  });

  const handleBlockerConfirm = useCallback(() => {
    onClose();
    blocker.proceed?.();
  }, [blocker, onClose]);

  const handleBlockerCancel = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  // Show loading state while:
  // 1. Initial data is loading and draft is not initialized
  // 2. Draft is being reinitialized (isInitialized will be false when draft is stale)
  if (!isInitialized || isInitLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <RemoveWidgetProvider value={removeWidgetValue}>
      <Backdrop
        open={isSaving || isDeletingDashboard}
        sx={{
          zIndex: Z_INDEX.loadingBackdrop
        }}
      >
        <CircularProgress />
      </Backdrop>
      <ConfirmationDialog
        open={discardDialog.isOpen}
        {...presets.discard}
        onCancel={discardDialog.close}
        onConfirm={() => {
          handleClose();
          discardDialog.close();
        }}
      />
      <ConfirmationDialog
        open={removeWidgetDialog.isOpen}
        {...presets.removeWidget}
        onCancel={removeWidgetDialog.close}
        onConfirm={() => {
          if (removeWidgetCallback) {
            removeWidgetCallback();
            setRemoveWidgetCallback(null);
          }
          removeWidgetDialog.close();
        }}
      />
      <ConfirmationDialog
        open={removeAllDialog.isOpen}
        {...presets.removeAll}
        onCancel={removeAllDialog.close}
        onConfirm={handleRemoveAll}
      />
      <ConfirmationDialog
        open={removeDashboardDialog.isOpen}
        {...presets.removeDashboard}
        onCancel={removeDashboardDialog.close}
        onConfirm={() => {
          handleRemoveDashboard();
          removeDashboardDialog.close();
        }}
      />
      <ConfirmationDialog
        open={blocker.state === 'blocked'}
        {...presets.discard}
        onCancel={handleBlockerCancel}
        onConfirm={handleBlockerConfirm}
      />
      <DragDropProvider
        plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
        onDragEnd={handleTabDragEnd}
      >
        <DashboardLayout
          disableTabRipple
          activeTab={currentTabIndex}
          minTabWidth={50}
          renderRight={
            <EditActionButtons
              isPending={isSaving || isDeletingDashboard}
              isSaveDisabled={isSaveDisabled}
              onAddApp={handleAddAppClick}
              onDiscard={handleDiscardClick}
              onRemoveAll={removeAllDialog.open}
              onRemoveDashboard={removeDashboardDialog.open}
              onSave={handleSave}
            />
          }
          tabNames={draftTabNames}
          tabsConfig={tabsConfig}
          title={<EditDashboardName onValidationChange={setHasDashboardNameErrors} />}
          onAddTab={handleAddTabDraft}
          onTabChange={handleDraftTabChange}
        >
          <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <EditBanner />
              <TilesOrder preview={previewData} />
              <AppsOrder
                preview={previewData}
                onTabCreated={handleTabCreated}
              />
            </Box>
          </PageInformationContext>
        </DashboardLayout>
      </DragDropProvider>
    </RemoveWidgetProvider>
  );
};
